import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { Usage } from '../lib/billing';
import { money } from '../lib/time';
import { UNSET } from '../lib/unset';

// Overview stat band (spec §5 / mock Stats): metric-grid of cards — repo count
// from the repos list + the live usage rollup. Presenter only; containers pass
// already-resolved values. Cells show "—" until their source resolves (loading
// / no session / API down) so the band never blanks out.

export type OverviewStatsProps = {
  /** Number of enabled repos, or `null` while the list is loading. */
  readonly repoCount: number | null;
  /** Current-window usage rollup, or `undefined` until it resolves. */
  readonly usage: Usage | undefined;
};

export function OverviewStats(props: OverviewStatsProps): JSX.Element {
  return (
    <ul class="metric-grid metric-grid--3" aria-label={t('overview.aria')}>
      <li class="card">
        <div class="card__content">
          <p class="metric-grid__label">{t('overview.repos')}</p>
          <p class="metric-grid__value">{props.repoCount ?? UNSET}</p>
        </div>
      </li>

      <li class="card">
        <div class="card__content">
          <p class="metric-grid__label">{t('overview.handled')}</p>
          <p class="metric-grid__value">
            <Show when={props.usage} fallback={UNSET}>
              {(u) => u().handled_units}
            </Show>
          </p>
        </div>
      </li>

      <li class="card">
        <div class="card__content">
          <p class="metric-grid__label">{t('overview.spend_cap')}</p>
          <p class="metric-grid__value">
            <Show when={props.usage} fallback={UNSET}>
              {/* #2057: null = the account set no budget; there is no platform
                  default cap to fall back on, so show the placeholder. */}
              {(u) => {
                const cap = u().spend_cap_usd;
                return cap === null ? UNSET : money(cap);
              }}
            </Show>
          </p>
        </div>
      </li>
    </ul>
  );
}
